import { createContext, useCallback, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import api from '../services/api';

interface GeneralSettings {
  platform_name: string;
  platform_tagline: string | null;
  platform_description: string | null;
  support_email: string | null;
  support_phone: string | null;
  token_to_dollar_rate: number;
  currency_symbol: string;
  currency_code: string;
  min_token_purchase: number;
  max_token_purchase: number;
  platform_commission_rate: number;
  enable_auctions: boolean;
  enable_games: boolean;
  enable_streaming: boolean;
  enable_advertising: boolean;
  enable_referrals: boolean;
  referral_bonus_amount: number;
  referral_bonus_for_referrer: number;
  maintenance_mode: boolean;
  maintenance_message: string | null;
  facebook_url: string | null;
  twitter_url: string | null;
  instagram_url: string | null;
  linkedin_url: string | null;
  youtube_url: string | null;
  meta_title: string | null;
  meta_description: string | null;
  meta_keywords: string | null;
  terms_url: string | null;
  privacy_url: string | null;
  logo_url: string | null;
  favicon_url: string | null;
  game_branding_url: string | null;
  default_language: string;
  supported_languages: string[];
  enable_recaptcha: boolean;
  recaptcha_site_key: string | null;
  sms_notifications_enabled: boolean;
}

interface SettingsContextType {
  settings: GeneralSettings | null;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
  formatTokensToDollars: (tokens: number) => string;
  formatCurrency: (amount: number) => string;
}

const defaultSettings: GeneralSettings = {
  platform_name: '',
  platform_tagline: null,
  platform_description: null,
  support_email: null,
  support_phone: null,
  token_to_dollar_rate: 1.0,
  currency_symbol: '$',
  currency_code: 'USD',
  min_token_purchase: 5.0,
  max_token_purchase: 10000.0,
  platform_commission_rate: 10.0,
  enable_auctions: true,
  enable_games: true,
  enable_streaming: true,
  enable_advertising: true,
  enable_referrals: true,
  referral_bonus_amount: 10.0,
  referral_bonus_for_referrer: 10.0,
  maintenance_mode: false,
  maintenance_message: null,
  facebook_url: null,
  twitter_url: null,
  instagram_url: null,
  linkedin_url: null,
  youtube_url: null,
  meta_title: null,
  meta_description: null,
  meta_keywords: null,
  terms_url: null,
  privacy_url: null,
  logo_url: null,
  favicon_url: null,
  game_branding_url: null,
  default_language: 'en',
  supported_languages: ['en'],
  enable_recaptcha: false,
  recaptcha_site_key: null,
  sms_notifications_enabled: false,
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<GeneralSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pendingRequest = useRef<Promise<GeneralSettings> | null>(null);

  const fetchSettings = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      // Focus, visibility, timer and manual refreshes can overlap. Share only
      // the active request; the next refresh still fetches fresh settings.
      if (!pendingRequest.current) {
        pendingRequest.current = api.get('/public/settings', {
          params: { _ts: Date.now() },
        }).then((response) => response.data as GeneralSettings)
          .finally(() => { pendingRequest.current = null; });
      }
      setSettings(await pendingRequest.current);
    } catch (err: any) {
      console.error('Failed to fetch settings:', err);
      setError('Failed to load platform settings');
      if (!silent) {
        setSettings(defaultSettings);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const refreshVisibleSettings = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      void fetchSettings(true);
    };

    window.addEventListener('focus', refreshVisibleSettings);
    document.addEventListener('visibilitychange', refreshVisibleSettings);
    const interval = window.setInterval(refreshVisibleSettings, 60000);

    return () => {
      window.removeEventListener('focus', refreshVisibleSettings);
      document.removeEventListener('visibilitychange', refreshVisibleSettings);
      window.clearInterval(interval);
    };
  }, [fetchSettings]);

  const formatTokensToDollars = (tokens: number): string => {
    if (!settings) return `$${tokens.toFixed(2)}`;
    const dollars = tokens * settings.token_to_dollar_rate;
    return `${settings.currency_symbol}${dollars.toFixed(2)} ${settings.currency_code}`;
  };

  const formatCurrency = (amount: number): string => {
    if (!settings) return `$${amount.toFixed(2)}`;
    return `${settings.currency_symbol}${amount.toFixed(2)}`;
  };

  const value: SettingsContextType = {
    settings: settings || defaultSettings,
    loading,
    error,
    refreshSettings: () => fetchSettings(false),
    formatTokensToDollars,
    formatCurrency,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
